import { ClaimSectionId } from './claim-editor-sections';

/**
 * Flatten nested claimValidationErrors (string | array) and drop duplicate / blank entries.
 */
export function flattenClaimValidationErrors(input: unknown): string[] {
    const out: string[] = [];
    const walk = (value: unknown): void => {
        if (value == null) return;
        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (trimmed) out.push(trimmed);
            return;
        }
        if (Array.isArray(value)) {
            value.forEach(walk);
        }
    };
    walk(input);
    return Array.from(new Set(out));
}

type JumpRule = { pattern: RegExp; sectionId: ClaimSectionId };

/**
 * Whole word or camelCase segment. `\b` does not split procedureCode / DiagnosisCode.
 * Next char may be another hump (`[A-Z]`), a non-identifier, or end of string.
 */
function identifierToken(token: string, plural = false): RegExp {
    const rest = token.slice(1).toLowerCase();
    const upper = token.charAt(0).toUpperCase();
    const lower = token.charAt(0).toLowerCase();
    const s = plural ? 's?' : '';
    const after = '(?=[A-Z]|[^A-Za-z0-9_]|$)';
    return new RegExp(
        `(?:[a-z0-9]${upper}${rest}${s}|(?:^|[^A-Za-z0-9_])[${lower}${upper}]${rest}${s})${after}`,
    );
}

/** Longest / most specific phrases first so "billing provider" wins over "provider". */
const JUMP_RULES: JumpRule[] = [
    { pattern: /\bbilling\s+provider\b/i, sectionId: 'billingProvider' },
    {
        pattern:
            /\b(rendering|attending|referring|supervising|ordering|operating|treating|purchasing)(\s+provider)?\b/i,
        sectionId: 'additionalProviders',
    },
    { pattern: /\bsubscriber\b/i, sectionId: 'subscriber' },
    // Before the generic payer rule: "third party administrator payer has been disabled" is a
    // Settings value (primary/secondary/tertiary TPA), not the subscriber's payer. Without this
    // the generic rule below catches it and jumps the operator to the wrong section.
    { pattern: /\bthird\s+party\s+administrator\s+payer\b/i, sectionId: 'settings' },
    { pattern: /\bpayer\b/i, sectionId: 'subscriber' },
    { pattern: /\bpatient\b/i, sectionId: 'patient' },
    { pattern: /\bguarantor\b/i, sectionId: 'guarantor' },
    { pattern: /\bfacility\b/i, sectionId: 'facility' },
    { pattern: identifierToken('procedure'), sectionId: 'services' },
    { pattern: identifierToken('modifier'), sectionId: 'services' },
    { pattern: identifierToken('service'), sectionId: 'services' },
    { pattern: identifierToken('code', true), sectionId: 'services' },
    { pattern: /\bprovider\b/i, sectionId: 'additionalProviders' },
];

/** Section to jump to for a server validation string, or undefined if none of the keywords match. */
export function resolveClaimValidationJump(message: string): ClaimSectionId | undefined {
    for (const rule of JUMP_RULES) {
        if (rule.pattern.test(message)) return rule.sectionId;
    }
    return undefined;
}

function stripLeadingSeparators(value: string): string {
    return value.replace(/^[\s:.\u2013\u2014\u2212-]+/, '').trim();
}

function messageStartsWithPhrase(messageLower: string, phraseLower: string): boolean {
    if (!phraseLower || !messageLower.startsWith(phraseLower)) return false;
    const next = messageLower.charAt(phraseLower.length);
    return next === '' || /[\s:.\u2013\u2014\u2212-]/.test(next);
}

/**
 * Remainder of a form-issue message after dropping a duplicated field label.
 * When the message already names the field, returns only the unique tail
 * ("First name is required" → "is required"). Otherwise keeps the current
 * em-dash prefix ("— This billing provider has been disabled…").
 */
export function formIssueRemainder(label: string, message: string): string {
    const labelTrim = label.trim();
    const messageTrim = message.trim();
    if (!messageTrim) return '';

    const messageLower = messageTrim.toLowerCase();
    const words = labelTrim.split(/\s+/).filter(Boolean);

    for (let i = 0; i < words.length; i++) {
        const phrase = words.slice(i).join(' ');
        if (phrase.length < 2) continue;
        if (messageStartsWithPhrase(messageLower, phrase.toLowerCase())) {
            return stripLeadingSeparators(messageTrim.slice(phrase.length));
        }
    }

    return `\u2014 ${messageTrim}`;
}
